/**
 * Recommendation Data Sources
 * Type-safe adapters for different recommendation sources
 */

import type { ShopifyProduct } from '../../types/shopify';
import { ShopifyAPI } from '../../utils/shopify-api';
import { RecentlyViewed } from '../../utils/storage';

export interface RecommendationSource {
  name: string;
  fetch(config: RecommendationConfig): Promise<ShopifyProduct[]>;
}

export interface RecommendationConfig {
  productId?: number;
  maxProducts?: number;
  handles?: string[];
  metafieldNamespace?: string;
  metafieldKey?: string;
}

/**
 * Complete the Set Source
 * Fetches products from metafield references or manual handles
 */
export class CompleteTheSetSource implements RecommendationSource {
  name = 'complete-the-set';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    const { handles, maxProducts = 4 } = config;

    if (!handles || handles.length === 0) {
      return [];
    }

    try {
      const products = await ShopifyAPI.getProducts(
        handles.slice(0, maxProducts)
      );
      return products;
    } catch (error) {
      console.error('Failed to fetch complete the set products:', error);
      return [];
    }
  }
}

/**
 * Recently Viewed Source
 * Fetches products from localStorage history
 */
export class RecentlyViewedSource implements RecommendationSource {
  name = 'recently-viewed';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    const { productId, maxProducts = 4 } = config;
    const viewedIds = RecentlyViewed.get();

    if (viewedIds.length === 0) {
      return [];
    }

    // Filter out current product
    const filtered = productId
      ? viewedIds.filter((id) => id !== productId)
      : viewedIds;

    // Get product handles from IDs (would need to fetch or have mapping)
    // For demo, we'll need product handles
    // In production, you'd store handles in localStorage too

    return [];
  }
}

/**
 * Trending Products Source
 * Fetches products from a manual list (theme settings)
 */
export class TrendingProductsSource implements RecommendationSource {
  name = 'trending';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    const { handles, maxProducts = 4 } = config;

    if (!handles || handles.length === 0) {
      return [];
    }

    try {
      const products = await ShopifyAPI.getProducts(
        handles.slice(0, maxProducts)
      );
      return products;
    } catch (error) {
      console.error('Failed to fetch trending products:', error);
      return [];
    }
  }
}

/**
 * Shopify Recommendations Source
 * Uses Shopify's built-in recommendation engine
 */
export class ShopifyRecommendationsSource implements RecommendationSource {
  name = 'shopify-recommendations';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    const { productId, maxProducts = 4 } = config;

    if (!productId) {
      return [];
    }

    try {
      return await ShopifyAPI.getRecommendations(productId, maxProducts);
    } catch (error) {
      console.error('Failed to fetch Shopify recommendations:', error);
      return [];
    }
  }
}

/**
 * Related by Collection Source
 * Fetches products from same collection
 */
export class RelatedByCollectionSource implements RecommendationSource {
  name = 'related-by-collection';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    // Would need collection handle and pagination
    // Simplified for demo
    return [];
  }
}

/**
 * Related by Tag Source
 * Fetches products with matching tags
 */
export class RelatedByTagSource implements RecommendationSource {
  name = 'related-by-tag';

  async fetch(config: RecommendationConfig): Promise<ShopifyProduct[]> {
    // Would need tag matching logic
    // Simplified for demo
    return [];
  }
}

/**
 * Data Source Factory
 * Creates appropriate source based on type
 */
export class RecommendationSourceFactory {
  private static sources = new Map<string, RecommendationSource>([
    ['complete_set', new CompleteTheSetSource()],
    ['recently_viewed', new RecentlyViewedSource()],
    ['trending', new TrendingProductsSource()],
    ['shopify_recommendations', new ShopifyRecommendationsSource()],
    ['related_by_collection', new RelatedByCollectionSource()],
    ['related_by_tag', new RelatedByTagSource()],
  ]);

  static getSource(type: string): RecommendationSource | null {
    return this.sources.get(type) || null;
  }

  static registerSource(type: string, source: RecommendationSource): void {
    this.sources.set(type, source);
  }
}
